import {useState} from 'react';

// Array drafts: the only legal update is a full replacement array.
export interface ArrayDraftState<TItem> {
  data: Array<TItem>;
  updateDraftData: (update: Array<TItem>) => void;
  isEditing: boolean;
  startEditing: () => void;
  stopEditing: () => void;
}

// Object drafts: updates are partials, merged into the current draft.
export interface ObjectDraftState<TData> {
  data: TData;
  updateDraftData: (update: Partial<TData>) => void;
  isEditing: boolean;
  startEditing: () => void;
  stopEditing: () => void;
}

// For compatibility- the old "DraftState" was only for objects
export type DraftState<TData> = ObjectDraftState<TData>;

export default function useDraftState<TItem>(initialData: Array<TItem>): ArrayDraftState<TItem>;
export default function useDraftState<TData>(initialData: TData): ObjectDraftState<TData>;

// Implementation signature: broad, NOT visible to callers.
export default function useDraftState<TData>(
  initialData: TData
): ObjectDraftState<TData> | ArrayDraftState<unknown> {
  const [draftData, setDraftData] = useState(initialData);
  const [isEditing, setIsEditing] = useState(false);
  return {
    data: isEditing ? draftData : initialData,
    updateDraftData: (update: Partial<TData> | TData) => {
      if (Array.isArray(update)) {
        setDraftData(update as TData);
      } else {
        setDraftData((current) => ({...current, ...update}));
      }
    },
    isEditing,
    startEditing: () => {
      // Whenever we start editing, our draft state needs to be re-set to the current value of initialData
      if (!isEditing) {
        setDraftData(initialData);
        setIsEditing(true);
      }
    },
    stopEditing: () => {
      setIsEditing(false);
    },
  } as ObjectDraftState<TData> | ArrayDraftState<unknown>;
}
